import { apiClient } from '@/shared/api/apiClient';
import { atom, getDefaultStore } from 'jotai';
import type { ApiTypes } from 'quadratic-shared/typesAndSchemas';

export type ClientDataKv = NonNullable<ApiTypes['/v0/user/client-data-kv.GET.response']['clientDataKv']>;
export type ClientDataKvPartial = Partial<ClientDataKv>;

export type ClientDataKvStatus = 'empty' | 'loading' | 'loaded' | 'error';

export interface ClientDataKvState {
  status: ClientDataKvStatus;
  clientDataKv: ClientDataKv;
  error: string | null;
  lastLoadedAt: number | null;
}

const defaultClientDataKvState: ClientDataKvState = {
  status: 'empty',
  clientDataKv: {},
  error: null,
  lastLoadedAt: null,
};

/**
 * Single source of truth for the logged-in user's `clientDataKv` blob.
 *
 * The KV is a small JSON bag of per-user flags (e.g. `lastSeenChangelogVersion`,
 * `knowsAboutModelPicker`, `featureWalkthroughCompleted`). Both the dashboard
 * and file loaders seed this atom via `loadClientDataKv`, and all mutations
 * should flow through `updateClientDataKvOptimistic` so UI updates are instant
 * and automatically reverted on failure.
 */
export const clientDataKvAtom = atom<ClientDataKvState>(defaultClientDataKvState);

/**
 * Loads the user's `clientDataKv` into the atom. Safe to call from multiple
 * loaders: when already loaded and `forceRefresh` is false, this is a no-op.
 */
export const loadClientDataKv = async (forceRefresh = false): Promise<void> => {
  const store = getDefaultStore();
  const currentState = store.get(clientDataKvAtom);

  if (currentState.status === 'loaded' && !forceRefresh) {
    return;
  }

  store.set(clientDataKvAtom, {
    ...currentState,
    status: 'loading',
    error: null,
  });

  try {
    const { clientDataKv } = await apiClient.user.clientDataKv.get();
    store.set(clientDataKvAtom, {
      status: 'loaded',
      clientDataKv: clientDataKv ?? {},
      error: null,
      lastLoadedAt: Date.now(),
    });
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error loading clientDataKv';
    console.warn('[loadClientDataKv] Failed:', errorMessage);
    store.set(clientDataKvAtom, {
      ...currentState,
      status: 'error',
      error: errorMessage,
    });
  }
};

/**
 * Optimistically merges `partial` into the atom, persists it to the server,
 * and either (a) replaces the atom with the server response on success or
 * (b) reverts the affected keys to their prior values on failure.
 *
 * Concurrent calls are safe: each call only rolls back the keys it touched,
 * leaving other in-flight optimistic updates intact.
 */
export const updateClientDataKvOptimistic = async (partial: ClientDataKvPartial): Promise<void> => {
  const store = getDefaultStore();
  const prevState = store.get(clientDataKvAtom);

  // Snapshot prior values for just the keys we're changing, so a rollback
  // doesn't clobber other concurrent optimistic updates.
  const touchedKeys = Object.keys(partial) as (keyof ClientDataKv)[];
  const prevKv = prevState.clientDataKv;

  store.set(clientDataKvAtom, {
    ...prevState,
    status: prevState.status === 'empty' ? 'loaded' : prevState.status,
    clientDataKv: { ...prevKv, ...partial },
  });

  try {
    const serverKv = await apiClient.user.clientDataKv.update(partial);
    // Merge the server response over whatever is currently in the atom so we
    // preserve any unrelated optimistic updates that happened mid-flight.
    const current = store.get(clientDataKvAtom);
    store.set(clientDataKvAtom, {
      status: 'loaded',
      clientDataKv: { ...current.clientDataKv, ...serverKv },
      error: null,
      lastLoadedAt: Date.now(),
    });
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error updating clientDataKv';
    console.warn('[updateClientDataKvOptimistic] Failed:', errorMessage);
    // Revert only the keys this call touched, leaving other concurrent
    // optimistic updates intact.
    const current = store.get(clientDataKvAtom);
    const reverted = { ...current.clientDataKv } as Record<string, unknown>;
    for (const key of touchedKeys) {
      const prior = (prevKv as Record<string, unknown>)[key];
      if (prior === undefined) {
        delete reverted[key];
      } else {
        reverted[key] = prior;
      }
    }
    store.set(clientDataKvAtom, {
      ...current,
      clientDataKv: reverted as ClientDataKv,
    });
    throw error;
  }
};

/** Snapshot the current clientDataKv state outside of React. */
export const getClientDataKvState = (): ClientDataKvState => getDefaultStore().get(clientDataKvAtom);

/** Reset the atom (used during sign-out / full app resets). */
export const resetClientDataKvState = (): void => {
  getDefaultStore().set(clientDataKvAtom, defaultClientDataKvState);
};
